/**
 * Pure reducer/derivation for {@link useOrganisationStatus} (`./use_organisation_status.ts`),
 * factored out for `bun:test` — same "pure function + thin hook" split
 * `use_session_facts.ts`'s `resolveSessionFactsState` uses.
 */
import type { OAuthEntityContext } from '../lib/auth/oauth_entity_context.js';

export interface OrganisationStatusPayload {
  connected: boolean;
  has_payroll_entitlement: boolean;
  company_id: string | null;
}

export type OrganisationStatusState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'error' }
  | {
      status: 'ready';
      connected: boolean;
      has_payroll_entitlement: boolean;
      company_id: string | null;
    };

/**
 * Priority: `no_entity` always wins (a caller whose grant just lost its
 * entity binding must see `no_entity`, not a leftover `error` from before the
 * context changed) — then a resolved payload wins over a `failed` flag (a
 * successful retry's data must display even if a stale `failed` flag from an
 * earlier attempt was never explicitly cleared by the caller) — then
 * `failed` — then `loading`. Mirrors `resolveSessionFactsState`.
 */
export function resolveOrganisationStatusState(input: {
  entityContext: OAuthEntityContext;
  data: OrganisationStatusPayload | undefined;
  failed: boolean;
}): OrganisationStatusState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.data !== undefined) {
    return {
      status: 'ready',
      connected: input.data.connected,
      has_payroll_entitlement: input.data.has_payroll_entitlement,
      company_id: input.data.company_id,
    };
  }
  if (input.failed) return { status: 'error' };
  return { status: 'loading' };
}
